/// <reference types="./index.d.ts" />
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { styled, Box, InputBase, NoSsr, SvgIcon, createTheme, ThemeProvider, Paper } from '@mui/material';
import React, { createContext as createContext$1, useRef, createElement, useContext, useMemo, useState, useCallback, useEffect, memo } from 'react';
import { Close, Check, ContentCopy, Edit, ExpandMore, ChevronRight } from '@mui/icons-material';
import create, { useStore } from 'zustand';
import copyToClipboard from 'copy-to-clipboard';

function createContext() {
  const ZustandContext = createContext$1(void 0);
  const Provider = ({
    createStore,
    children
  }) => {
    const storeRef = useRef();
    if (!storeRef.current) {
      storeRef.current = createStore();
    }
    return createElement(
      ZustandContext.Provider,
      { value: storeRef.current },
      children
    );
  };
  const useContextStore = (selector, equalityFn) => {
    const store = useContext(ZustandContext);
    if (!store) {
      throw new Error(
        "Seems like you have not used zustand provider as an ancestor."
      );
    }
    return useStore(
      store,
      selector,
      equalityFn
    );
  };
  const useStoreApi = () => {
    const store = useContext(ZustandContext);
    if (!store) {
      throw new Error(
        "Seems like you have not used zustand provider as an ancestor."
      );
    }
    return useMemo(() => ({ ...store }), [store]);
  };
  return {
    Provider,
    useStore: useContextStore,
    useStoreApi
  };
}

const combine = (initialState, create) => (...a) => Object.assign({}, initialState, create(...a));

const lightColorspace = {
    scheme: 'Light Theme',
    author: 'mac gainor (https://github.com/mac-s-g)',
    base00: 'rgba(0, 0, 0, 0)',
    base01: 'rgb(245, 245, 245)',
    base02: 'rgb(235, 235, 235)',
    base03: '#93a1a1',
    base04: 'rgba(0, 0, 0, 0.3)',
    base05: '#586e75',
    base06: '#073642',
    base07: '#002b36',
    base08: '#d33682',
    base09: '#cb4b16',
    base0A: '#dc322f',
    base0B: '#859900',
    base0C: '#6c71c4',
    base0D: '#586e75',
    base0E: '#2aa198',
    base0F: '#268bd2'
};
const darkColorspace = {
    scheme: 'Dark Theme',
    author: 'Chris Kempson (http://chriskempson.com)',
    base00: '#181818',
    base01: '#282828',
    base02: '#383838',
    base03: '#585858',
    base04: '#b8b8b8',
    base05: '#d8d8d8',
    base06: '#e8e8e8',
    base07: '#f8f8f8',
    base08: '#ab4642',
    base09: '#dc9656',
    base0A: '#f7ca88',
    base0B: '#a1b56c',
    base0C: '#86c1b9',
    base0D: '#7cafc2',
    base0E: '#ba8baf',
    base0F: '#a16946'
};

const DefaultKeyRenderer = ()=>null;
DefaultKeyRenderer.when = ()=>false;
const createJsonViewerStore = (props)=>create(combine({
        // provided by user
        enableClipboard: props.enableClipboard ?? true,
        indentWidth: props.indentWidth ?? 3,
        groupArraysAfterLength: props.groupArraysAfterLength ?? 100,
        collapseStringsAfterLength: props.collapseStringsAfterLength === false ? Number.MAX_VALUE : props.collapseStringsAfterLength ?? 50,
        maxDisplayLength: props.maxDisplayLength ?? 30,
        rootName: props.rootName ?? 'root',
        onChange: props.onChange ?? (()=>{}),
        keyRenderer: props.keyRenderer ?? DefaultKeyRenderer,
        editable: props.editable ?? false,
        defaultInspectDepth: props.defaultInspectDepth ?? 5,
        objectSortKeys: props.objectSortKeys ?? false,
        quotesOnKeys: props.quotesOnKeys ?? true,
        displayDataTypes: props.displayDataTypes ?? true,
        // internal state
        inspectCache: {},
        hoverPath: null,
        colorspace: lightColorspace,
        value: props.value,
        displayObjectSize: props.displayObjectSize ?? true
    }, (set, get)=>({
            getInspectCache: (path, nestedIndex)=>{
                const target = nestedIndex !== undefined ? path.join('.') + `[${nestedIndex}]nt` : path.join('.');
                return get().inspectCache[target];
            },
            setInspectCache: (path, action, nestedIndex)=>{
                const target = nestedIndex !== undefined ? path.join('.') + `[${nestedIndex}]nt` : path.join('.');
                set((state)=>({
                        inspectCache: {
                            ...state.inspectCache,
                            [target]: typeof action === 'function' ? action(state.inspectCache[target]) : action
                        }
                    }));
            },
            setHover: (path, nestedIndex)=>{
                set({
                    hoverPath: path ? {
                        path,
                        nestedIndex
                    } : null
                });
            }
        })));
const { useStore: useJsonViewerStore , useStoreApi: useJsonViewerStoreApi , Provider: JsonViewerProvider  } = createContext();

const useTextColor = ()=>{
    return useJsonViewerStore((store)=>store.colorspace.base07);
};

/**
 * useClipboard hook accepts one argument options in which copied status timeout duration is defined (defaults to 2000). Hook returns object with properties:
 * - copy – function to copy value to clipboard
 * - copied – value that indicates that copy handler was called less than options.timeout ms ago
 * - reset – function to clear timeout and reset copied to false
 */ function useClipboard({ timeout =2000  } = {}) {
    const [copied, setCopied] = useState(false);
    const copyTimeout = useRef(null);
    const handleCopyResult = useCallback((value)=>{
        const current = copyTimeout.current;
        if (current) {
            window.clearTimeout(current);
        }
        copyTimeout.current = window.setTimeout(()=>setCopied(false), timeout);
        setCopied(value);
    }, [
        timeout
    ]);
    const copy = useCallback((valueToCopy)=>{
        if ('clipboard' in navigator) {
            navigator.clipboard.writeText(valueToCopy).then(()=>handleCopyResult(true))// When navigator.clipboard throws an error, fallback to copy-to-clipboard package
            .catch(()=>copyToClipboard(valueToCopy));
        } else {
            // fallback to copy-to-clipboard when navigator.clipboard is not available
            copyToClipboard(valueToCopy);
        }
    }, [
        handleCopyResult
    ]);
    const reset = useCallback(()=>{
        setCopied(false);
        if (copyTimeout.current) {
            clearTimeout(copyTimeout.current);
        }
    }, []);
    return {
        copy,
        reset,
        copied
    };
}

const applyValue = (obj, path, value)=>{
    if (typeof obj !== 'object' || obj === null) {
        if (path.length !== 0) {
            throw new Error('path is incorrect');
        }
        return value;
    }
    const arr = [
        ...path
    ];
    let key;
    if (path.length > 0) {
        key = arr[0];
        if (key === '__proto__') {
            throw new TypeError('don\'t modify __proto__!!!');
        }
        if (arr.length > 1) {
            arr.shift();
            obj[key] = applyValue(obj[key], arr, value);
        } else {
            obj[key] = value;
        }
    }
    return obj;
};
function createDataType(is, Component, Editor, PreComponent, PostComponent) {
    return {
        is,
        Component,
        Editor,
        PreComponent,
        PostComponent
    };
}
const isCycleReference = (root, path, value)=>{
    if (root === null || value === null) {
        return false;
    }
    if (typeof root !== 'object') {
        return false;
    }
    if (typeof value !== 'object') {
        return false;
    }
    if (Object.is(root, value) && path.length !== 0) {
        return '';
    }
    const currentPath = [];
    const arr = [
        ...path
    ];
    let currentRoot = root;
    while(currentRoot !== value || arr.length !== 0){
        if (typeof currentRoot !== 'object' || currentRoot === null) {
            return false;
        }
        if (Object.is(currentRoot, value)) {
            return currentPath.reduce((path, value, currentIndex)=>{
                if (typeof value === 'number') {
                    return path + `[${value}]`;
                }
                return path + `${currentIndex === 0 ? '' : '.'}${value}`;
            }, '');
        }
        const target = arr.shift();
        currentPath.push(target);
        currentRoot = currentRoot[target];
    }
    return false;
};

function useIsCycleReference(path, value) {
    const rootValue = useJsonViewerStore((store)=>store.value);
    return useMemo(()=>isCycleReference(rootValue, path, value), [
        path,
        value,
        rootValue
    ]);
}

function useInspect(path, value, nestedIndex) {
    const depth = path.length;
    const isTrap = useIsCycleReference(path, value);
    const getInspectCache = useJsonViewerStore((store)=>store.getInspectCache);
    const setInspectCache = useJsonViewerStore((store)=>store.setInspectCache);
    const defaultInspectDepth = useJsonViewerStore((store)=>store.defaultInspectDepth);
    useEffect(()=>{
        const inspect = getInspectCache(path, nestedIndex);
        if (inspect !== undefined) {
            return;
        }
        if (nestedIndex !== undefined) {
            setInspectCache(path, false, nestedIndex);
        } else {
            // do not inspect when it is a cycle reference, otherwise there will have a loop
            const inspect1 = isTrap ? false : depth < defaultInspectDepth;
            setInspectCache(path, inspect1);
        }
    }, [
        defaultInspectDepth,
        depth,
        getInspectCache,
        isTrap,
        nestedIndex,
        path,
        setInspectCache
    ]);
    const [inspect, set] = useState(()=>{
        const shouldInspect = getInspectCache(path, nestedIndex);
        if (shouldInspect !== undefined) {
            return shouldInspect;
        }
        if (nestedIndex !== undefined) {
            return false;
        }
        return isTrap ? false : depth < defaultInspectDepth;
    });
    const setInspect = useCallback((apply)=>{
        set((oldState)=>{
            const newState = typeof apply === 'boolean' ? apply : apply(oldState);
            setInspectCache(path, newState, nestedIndex);
            return newState;
        });
    }, [
        nestedIndex,
        path,
        setInspectCache
    ]);
    return [
        inspect,
        setInspect
    ];
}

/// <reference types="./utils.d.ts" />
class DevelopmentError extends Error {
    constructor(message) {
        super(message
            ? `${message}\n`
            : '' +
                'The behavior is not expected and not allowed.\n' +
                'If you see this in development, please fix the error ASAP.\n' +
                'If you see this in production, please contract our customer service.\n');
    }
}

const DataBox = styled((props)=>/*#__PURE__*/ jsx(Box, {
        component: "div",
        ...props
    }))`
  display: inline-block;
`;

const DataTypeLabel = ({ dataType , enable =true  })=>{
    if (!enable) {
        return null;
    }
    return /*#__PURE__*/ jsx(DataBox, {
        className: "data-type-label",
        sx: {
            mx: 0.5,
            fontSize: '0.7rem',
            opacity: 0.8
        },
        children: dataType
    });
};

function createEasyType(type, renderValue, config) {
    const displayTypeLabel = config.displayTypeLabel ?? true;
    const Render = /*#__PURE__*/ React.memo(renderValue);
    const EasyType = (props)=>{
        const storeDisplayDataTypes = useJsonViewerStore((store)=>store.displayDataTypes);
        const color = useJsonViewerStore((store)=>store.colorspace[config.colorKey]);
        return /*#__PURE__*/ jsxs(DataBox, {
            sx: {
                color
            },
            children: [
                displayTypeLabel && storeDisplayDataTypes && /*#__PURE__*/ jsx(DataTypeLabel, {
                    dataType: type
                }),
                /*#__PURE__*/ jsx(DataBox, {
                    className: `${type}-value`,
                    children: /*#__PURE__*/ jsx(Render, {
                        value: props.value
                    })
                })
            ]
        });
    };
    EasyType.displayName = `easy-${type}-type`;
    if (!config.fromString) {
        return {
            Component: EasyType
        };
    }
    const fromString = config.fromString;
    const EasyTypeEditor = ({ value , setValue  })=>{
        const color = useJsonViewerStore((store)=>store.colorspace[config.colorKey]);
        return /*#__PURE__*/ jsx(InputBase, {
            value: `${value}`,
            onChange: useCallback((event)=>{
                const value = fromString(event.target.value);
                setValue(value);
            }, [
                setValue
            ]),
            size: "small",
            multiline: true,
            sx: {
                color,
                padding: 0.5,
                borderStyle: 'solid',
                borderColor: 'black',
                borderWidth: 1,
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                display: 'inline-flex'
            }
        });
    };
    EasyTypeEditor.displayName = `easy-${type}-type-editor`;
    return {
        Component: EasyType,
        Editor: EasyTypeEditor
    };
}

const functionBody = (func)=>{
    const funcString = func.toString();
    let isUsualFunction = true;
    const parenthesisPos = funcString.indexOf(')');
    const arrowPos = funcString.indexOf('=>');
    if (arrowPos !== -1 && arrowPos > parenthesisPos) {
        isUsualFunction = false;
    }
    if (isUsualFunction) {
        return funcString.substring(funcString.indexOf('{', parenthesisPos) + 1, funcString.lastIndexOf('}'));
    }
    return funcString.substring(funcString.indexOf('=>') + 2);
};
const functionName = (func)=>{
    const funcString = func.toString();
    const isUsualFunction = funcString.indexOf('function') !== -1;
    if (isUsualFunction) {
        return funcString.substring(8, funcString.indexOf('{')).trim();
    }
    return funcString.substring(0, funcString.indexOf('=>') + 2).trim();
};
const lb = '{';
const rb = '}';
const PreFunctionType = (props)=>{
    return /*#__PURE__*/ jsxs(NoSsr, {
        children: [
            /*#__PURE__*/ jsx(DataTypeLabel, {
                dataType: "function"
            }),
            /*#__PURE__*/ jsxs(Box, {
                component: "span",
                className: "data-function-start",
                sx: {
                    letterSpacing: 0.5
                },
                children: [
                    functionName(props.value),
                    ' ',
                    lb
                ]
            })
        ]
    });
};
const PostFunctionType = ()=>{
    return /*#__PURE__*/ jsx(NoSsr, {
        children: /*#__PURE__*/ jsx(Box, {
            component: "span",
            className: "data-function-end",
            children: rb
        })
    });
};
const FunctionType = (props)=>{
    const functionColor = useJsonViewerStore((store)=>store.colorspace.base05);
    return /*#__PURE__*/ jsx(NoSsr, {
        children: /*#__PURE__*/ jsx(Box, {
            className: "data-function",
            sx: {
                display: props.inspect ? 'block' : 'inline-block',
                pl: props.inspect ? 2 : 0,
                color: functionColor
            },
            children: props.inspect ? functionBody(props.value) : /*#__PURE__*/ jsx(Box, {
                component: "span",
                className: "data-function-body",
                onClick: ()=>props.setInspect(true),
                sx: {
                    '&:hover': {
                        cursor: 'pointer'
                    }
                },
                children: "..."
            })
        })
    });
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var dist = {};

var group$1 = {};

var deepEql = {exports: {}};

var typeDetect = {exports: {}};

(function (module, exports) {
	(function (global, factory) {
		module.exports = factory() ;
	}(commonjsGlobal, (function () {
	/* !
	 * type-detect
	 * Copyright(c) 2013 jake luer <jake@alogicalparadox.com>
	 * MIT Licensed
	 */
	var promiseExists = typeof Promise === 'function';

	/* eslint-disable no-undef */
	var globalObject = typeof self === 'object' ? self : commonjsGlobal; // eslint-disable-line id-blacklist

	var symbolExists = typeof Symbol !== 'undefined';
	var mapExists = typeof Map !== 'undefined';
	var setExists = typeof Set !== 'undefined';
	var weakMapExists = typeof WeakMap !== 'undefined';
	var weakSetExists = typeof WeakSet !== 'undefined';
	var dataViewExists = typeof DataView !== 'undefined';
	var symbolIteratorExists = symbolExists && typeof Symbol.iterator !== 'undefined';
	var symbolToStringTagExists = symbolExists && typeof Symbol.toStringTag !== 'undefined';
	var setEntriesExists = setExists && typeof Set.prototype.entries === 'function';
	var mapEntriesExists = mapExists && typeof Map.prototype.entries === 'function';
	var setIteratorPrototype = setEntriesExists && Object.getPrototypeOf(new Set().entries());
	var mapIteratorPrototype = mapEntriesExists && Object.getPrototypeOf(new Map().entries());
	var arrayIteratorExists = symbolIteratorExists && typeof Array.prototype[Symbol.iterator] === 'function';
	var arrayIteratorPrototype = arrayIteratorExists && Object.getPrototypeOf([][Symbol.iterator]());
	var stringIteratorExists = symbolIteratorExists && typeof String.prototype[Symbol.iterator] === 'function';
	var stringIteratorPrototype = stringIteratorExists && Object.getPrototypeOf(''[Symbol.iterator]());
	var toStringLeftSliceLength = 8;
	var toStringRightSliceLength = -1;
	/**
	 * ### typeOf (obj)
	 *
	 * Uses `Object.prototype.toString` to determine the type of an object,
	 * normalising behaviour across engine versions & well optimised.
	 *
	 * @param {Mixed} object
	 * @return {String} object type
	 * @api public
	 */
	function typeDetect(obj) {
	  /* ! Speed optimisation
	   * Pre:
	   *   string literal     x 3,039,035 ops/sec ±1.62% (78 runs sampled)
	   *   boolean literal    x 1,424,138 ops/sec ±4.54% (75 runs sampled)
	   *   number literal     x 1,653,153 ops/sec ±1.91% (82 runs sampled)
	   *   undefined          x 9,978,660 ops/sec ±1.92% (75 runs sampled)
	   *   function           x 2,556,769 ops/sec ±1.73% (77 runs sampled)
	   * Post:
	   *   string literal     x 38,564,796 ops/sec ±1.15% (79 runs sampled)
	   *   boolean literal    x 31,148,940 ops/sec ±1.10% (79 runs sampled)
	   *   number literal     x 32,679,330 ops/sec ±1.90% (78 runs sampled)
	   *   undefined          x 32,363,368 ops/sec ±1.07% (82 runs sampled)
	   *   function           x 31,296,870 ops/sec ±0.96% (83 runs sampled)
	   */
	  var typeofObj = typeof obj;
	  if (typeofObj !== 'object') {
	    return typeofObj;
	  }

	  /* ! Speed optimisation
	   * Pre:
	   *   null               x 28,645,765 ops/sec ±1.17% (82 runs sampled)
	   * Post:
	   *   null               x 36,428,962 ops/sec ±1.37% (84 runs sampled)
	   */
	  if (obj === null) {
	    return 'null';
	  }

	  /* ! Spec Conformance
	   * Test: `Object.prototype.toString.call(window)``
	   *  - Node === "[object global]"
	   *  - Chrome === "[object global]"
	   *  - Firefox === "[object Window]"
	   *  - PhantomJS === "[object Window]"
	   *  - Safari === "[object Window]"
	   *  - IE 11 === "[object Window]"
	   *  - IE Edge === "[object Window]"
	   * Test: `Object.prototype.toString.call(this)``
	   *  - Chrome Worker === "[object global]"
	   *  - Firefox Worker === "[object DedicatedWorkerGlobalScope]"
	   *  - Safari Worker === "[object DedicatedWorkerGlobalScope]"
	   *  - IE 11 Worker === "[object WorkerGlobalScope]"
	   *  - IE Edge Worker === "[object WorkerGlobalScope]"
	   */
	  if (obj === globalObject) {
	    return 'global';
	  }

	  /* ! Speed optimisation
	   * Pre:
	   *   array literal      x 2,888,352 ops/sec ±0.67% (82 runs sampled)
	   * Post:
	   *   array literal      x 22,479,650 ops/sec ±0.96% (81 runs sampled)
	   */
	  if (
	    Array.isArray(obj) &&
	    (symbolToStringTagExists === false || !(Symbol.toStringTag in obj))
	  ) {
	    return 'Array';
	  }

	  // Not caching existence of `window` and related properties due to potential
	  // for `window` to be unset before tests in quasi-browser environments.
	  if (typeof window === 'object' && window !== null) {
	    /* ! Spec Conformance
	     * (https://html.spec.whatwg.org/multipage/browsers.html#location)
	     * WhatWG HTML$7.7.3 - The `Location` interface
	     * Test: `Object.prototype.toString.call(window.location)``
	     *  - IE <=11 === "[object Object]"
	     *  - IE Edge <=13 === "[object Object]"
	     */
	    if (typeof window.location === 'object' && obj === window.location) {
	      return 'Location';
	    }

	    /* ! Spec Conformance
	     * (https://html.spec.whatwg.org/#document)
	     * WhatWG HTML$3.1.1 - The `Document` object
	     * Note: Most browsers currently adher to the W3C DOM Level 2 spec
	     *       (https://www.w3.org/TR/DOM-Level-2-HTML/html.html#ID-26809268)
	     *       which suggests that browsers should use HTMLTableCellElement for
	     *       both TD and TH elements. WhatWG separates these.
	     *       WhatWG HTML states:
	     *         > For historical reasons, Window objects must also have a
	     *         > writable, configurable, non-enumerable property named
	     *         > HTMLDocument whose value is the Document interface object.
	     * Test: `Object.prototype.toString.call(document)``
	     *  - Chrome === "[object HTMLDocument]"
	     *  - Firefox === "[object HTMLDocument]"
	     *  - Safari === "[object HTMLDocument]"
	     *  - IE <=10 === "[object Document]"
	     *  - IE 11 === "[object HTMLDocument]"
	     *  - IE Edge <=13 === "[object HTMLDocument]"
	     */
	    if (typeof window.document === 'object' && obj === window.document) {
	      return 'Document';
	    }

	    if (typeof window.navigator === 'object') {
	      /* ! Spec Conformance
	       * (https://html.spec.whatwg.org/multipage/webappapis.html#mimetypearray)
	       * WhatWG HTML$8.6.1.5 - Plugins - Interface MimeTypeArray
	       * Test: `Object.prototype.toString.call(navigator.mimeTypes)``
	       *  - IE <=10 === "[object MSMimeTypesCollection]"
	       */
	      if (typeof window.navigator.mimeTypes === 'object' &&
	          obj === window.navigator.mimeTypes) {
	        return 'MimeTypeArray';
	      }

	      /* ! Spec Conformance
	       * (https://html.spec.whatwg.org/multipage/webappapis.html#pluginarray)
	       * WhatWG HTML$8.6.1.5 - Plugins - Interface PluginArray
	       * Test: `Object.prototype.toString.call(navigator.plugins)``
	       *  - IE <=10 === "[object MSPluginsCollection]"
	       */
	      if (typeof window.navigator.plugins === 'object' &&
	          obj === window.navigator.plugins) {
	        return 'PluginArray';
	      }
	    }

	    if ((typeof window.HTMLElement === 'function' ||
	        typeof window.HTMLElement === 'object') &&
	        obj instanceof window.HTMLElement) {
	      /* ! Spec Conformance
	      * (https://html.spec.whatwg.org/multipage/webappapis.html#pluginarray)
	      * WhatWG HTML$4.4.4 - The `blockquote` element - Interface `HTMLQuoteElement`
	      * Test: `Object.prototype.toString.call(document.createElement('blockquote'))``
	      *  - IE <=10 === "[object HTMLBlockElement]"
	      */
	      if (obj.tagName === 'BLOCKQUOTE') {
	        return 'HTMLQuoteElement';
	      }

	      /* ! Spec Conformance
	       * (https://html.spec.whatwg.org/#htmltabledatacellelement)
	       * WhatWG HTML$4.9.9 - The `td` element - Interface `HTMLTableDataCellElement`
	       * Note: Most browsers currently adher to the W3C DOM Level 2 spec
	       *       (https://www.w3.org/TR/DOM-Level-2-HTML/html.html#ID-82915075)
	       *       which suggests that browsers should use HTMLTableCellElement for
	       *       both TD and TH elements. WhatWG separates these.
	       * Test: Object.prototype.toString.call(document.createElement('td'))
	       *  - Chrome === "[object HTMLTableCellElement]"
	       *  - Firefox === "[object HTMLTableCellElement]"
	       *  - Safari === "[object HTMLTableCellElement]"
	       */
	      if (obj.tagName === 'TD') {
	        return 'HTMLTableDataCellElement';
	      }

	      /* ! Spec Conformance
	       * (https://html.spec.whatwg.org/#htmltableheadercellelement)
	       * WhatWG HTML$4.9.9 - The `td` element - Interface `HTMLTableHeaderCellElement`
	       * Note: Most browsers currently adher to the W3C DOM Level 2 spec
	       *       (https://www.w3.org/TR/DOM-Level-2-HTML/html.html#ID-82915075)
	       *       which suggests that browsers should use HTMLTableCellElement for
	       *       both TD and TH elements. WhatWG separates these.
	       * Test: Object.prototype.toString.call(document.createElement('th'))
	       *  - Chrome === "[object HTMLTableCellElement]"
	       *  - Firefox === "[object HTMLTableCellElement]"
	       *  - Safari === "[object HTMLTableCellElement]"
	       */
	      if (obj.tagName === 'TH') {
	        return 'HTMLTableHeaderCellElement';
	      }
	    }
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   Float64Array       x 625,644 ops/sec ±1.58% (80 runs sampled)
	  *   Float32Array       x 1,279,852 ops/sec ±2.91% (77 runs sampled)
	  *   Uint32Array        x 1,178,185 ops/sec ±1.95% (83 runs sampled)
	  *   Uint16Array        x 1,008,380 ops/sec ±2.25% (80 runs sampled)
	  *   Uint8Array         x 1,128,040 ops/sec ±2.11% (81 runs sampled)
	  *   Int32Array         x 1,170,119 ops/sec ±2.88% (80 runs sampled)
	  *   Int16Array         x 1,176,348 ops/sec ±5.79% (86 runs sampled)
	  *   Int8Array          x 1,058,707 ops/sec ±4.94% (77 runs sampled)
	  *   Uint8ClampedArray  x 1,110,633 ops/sec ±4.20% (80 runs sampled)
	  * Post:
	  *   Float64Array       x 7,105,671 ops/sec ±13.47% (64 runs sampled)
	  *   Float32Array       x 5,887,912 ops/sec ±1.46% (82 runs sampled)
	  *   Uint32Array        x 6,491,661 ops/sec ±1.76% (79 runs sampled)
	  *   Uint16Array        x 6,559,795 ops/sec ±1.67% (82 runs sampled)
	  *   Uint8Array         x 6,463,966 ops/sec ±1.43% (85 runs sampled)
	  *   Int32Array         x 5,641,841 ops/sec ±3.49% (81 runs sampled)
	  *   Int16Array         x 6,583,511 ops/sec ±1.98% (80 runs sampled)
	  *   Int8Array          x 6,606,078 ops/sec ±1.74% (81 runs sampled)
	  *   Uint8ClampedArray  x 6,602,224 ops/sec ±1.77% (83 runs sampled)
	  */
	  var stringTag = (symbolToStringTagExists && obj[Symbol.toStringTag]);
	  if (typeof stringTag === 'string') {
	    return stringTag;
	  }

	  var objPrototype = Object.getPrototypeOf(obj);
	  /* ! Speed optimisation
	  * Pre:
	  *   regex literal      x 1,772,385 ops/sec ±1.85% (77 runs sampled)
	  *   regex constructor  x 2,143,634 ops/sec ±2.46% (78 runs sampled)
	  * Post:
	  *   regex literal      x 3,928,009 ops/sec ±0.65% (78 runs sampled)
	  *   regex constructor  x 3,931,108 ops/sec ±0.58% (84 runs sampled)
	  */
	  if (objPrototype === RegExp.prototype) {
	    return 'RegExp';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   date               x 2,130,074 ops/sec ±4.42% (68 runs sampled)
	  * Post:
	  *   date               x 3,953,779 ops/sec ±1.35% (77 runs sampled)
	  */
	  if (objPrototype === Date.prototype) {
	    return 'Date';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-promise.prototype-@@tostringtag)
	   * ES6$25.4.5.4 - Promise.prototype[@@toStringTag] should be "Promise":
	   * Test: `Object.prototype.toString.call(Promise.resolve())``
	   *  - Chrome <=47 === "[object Object]"
	   *  - Edge <=20 === "[object Object]"
	   *  - Firefox 29-Latest === "[object Promise]"
	   *  - Safari 7.1-Latest === "[object Promise]"
	   */
	  if (promiseExists && objPrototype === Promise.prototype) {
	    return 'Promise';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   set                x 2,222,186 ops/sec ±1.31% (82 runs sampled)
	  * Post:
	  *   set                x 4,545,879 ops/sec ±1.13% (83 runs sampled)
	  */
	  if (setExists && objPrototype === Set.prototype) {
	    return 'Set';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   map                x 2,396,842 ops/sec ±1.59% (81 runs sampled)
	  * Post:
	  *   map                x 4,183,945 ops/sec ±6.59% (82 runs sampled)
	  */
	  if (mapExists && objPrototype === Map.prototype) {
	    return 'Map';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   weakset            x 1,323,220 ops/sec ±2.17% (76 runs sampled)
	  * Post:
	  *   weakset            x 4,237,510 ops/sec ±2.01% (77 runs sampled)
	  */
	  if (weakSetExists && objPrototype === WeakSet.prototype) {
	    return 'WeakSet';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   weakmap            x 1,500,260 ops/sec ±2.02% (78 runs sampled)
	  * Post:
	  *   weakmap            x 3,881,384 ops/sec ±1.45% (82 runs sampled)
	  */
	  if (weakMapExists && objPrototype === WeakMap.prototype) {
	    return 'WeakMap';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-dataview.prototype-@@tostringtag)
	   * ES6$24.2.4.21 - DataView.prototype[@@toStringTag] should be "DataView":
	   * Test: `Object.prototype.toString.call(new DataView(new ArrayBuffer(1)))``
	   *  - Edge <=13 === "[object Object]"
	   */
	  if (dataViewExists && objPrototype === DataView.prototype) {
	    return 'DataView';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-%mapiteratorprototype%-@@tostringtag)
	   * ES6$23.1.5.2.2 - %MapIteratorPrototype%[@@toStringTag] should be "Map Iterator":
	   * Test: `Object.prototype.toString.call(new Map().entries())``
	   *  - Edge <=13 === "[object Object]"
	   */
	  if (mapExists && objPrototype === mapIteratorPrototype) {
	    return 'Map Iterator';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-%setiteratorprototype%-@@tostringtag)
	   * ES6$23.2.5.2.2 - %SetIteratorPrototype%[@@toStringTag] should be "Set Iterator":
	   * Test: `Object.prototype.toString.call(new Set().entries())``
	   *  - Edge <=13 === "[object Object]"
	   */
	  if (setExists && objPrototype === setIteratorPrototype) {
	    return 'Set Iterator';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-%arrayiteratorprototype%-@@tostringtag)
	   * ES6$22.1.5.2.2 - %ArrayIteratorPrototype%[@@toStringTag] should be "Array Iterator":
	   * Test: `Object.prototype.toString.call([][Symbol.iterator]())``
	   *  - Edge <=13 === "[object Object]"
	   */
	  if (arrayIteratorExists && objPrototype === arrayIteratorPrototype) {
	    return 'Array Iterator';
	  }

	  /* ! Spec Conformance
	   * (http://www.ecma-international.org/ecma-262/6.0/index.html#sec-%stringiteratorprototype%-@@tostringtag)
	   * ES6$21.1.5.2.2 - %StringIteratorPrototype%[@@toStringTag] should be "String Iterator":
	   * Test: `Object.prototype.toString.call(''[Symbol.iterator]())``
	   *  - Edge <=13 === "[object Object]"
	   */
	  if (stringIteratorExists && objPrototype === stringIteratorPrototype) {
	    return 'String Iterator';
	  }

	  /* ! Speed optimisation
	  * Pre:
	  *   object from null   x 2,424,320 ops/sec ±1.67% (76 runs sampled)
	  * Post:
	  *   object from null   x 5,838,000 ops/sec ±0.99% (84 runs sampled)
	  */
	  if (objPrototype === null) {
	    return 'Object';
	  }

	  return Object
	    .prototype
	    .toString
	    .call(obj)
	    .slice(toStringLeftSliceLength, toStringRightSliceLength);
	}

	return typeDetect;

	})));
} (typeDetect));

/* globals Symbol: false, Uint8Array: false, WeakMap: false */
/*!
 * deep-eql
 * Copyright(c) 2013 Jake Luer <jake@alogicalparadox.com>
 * MIT Licensed
 */

var type = typeDetect.exports;
function FakeMap() {
  this._key = 'chai/deep-eql__' + Math.random() + Date.now();
}

FakeMap.prototype = {
  get: function get(key) {
    return key[this._key];
  },
  set: function set(key, value) {
    if (Object.isExtensible(key)) {
      Object.defineProperty(key, this._key, {
        value: value,
        configurable: true,
      });
    }
  },
};

var MemoizeMap = typeof WeakMap === 'function' ? WeakMap : FakeMap;
/*!
 * Check to see if the MemoizeMap has recorded a result of the two operands
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {MemoizeMap} memoizeMap
 * @returns {Boolean|null} result
*/
function memoizeCompare(leftHandOperand, rightHandOperand, memoizeMap) {
  // Technically, WeakMap keys can *only* be objects, not primitives.
  if (!memoizeMap || isPrimitive(leftHandOperand) || isPrimitive(rightHandOperand)) {
    return null;
  }
  var leftHandMap = memoizeMap.get(leftHandOperand);
  if (leftHandMap) {
    var result = leftHandMap.get(rightHandOperand);
    if (typeof result === 'boolean') {
      return result;
    }
  }
  return null;
}

/*!
 * Set the result of the equality into the MemoizeMap
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {MemoizeMap} memoizeMap
 * @param {Boolean} result
*/
function memoizeSet(leftHandOperand, rightHandOperand, memoizeMap, result) {
  // Technically, WeakMap keys can *only* be objects, not primitives.
  if (!memoizeMap || isPrimitive(leftHandOperand) || isPrimitive(rightHandOperand)) {
    return;
  }
  var leftHandMap = memoizeMap.get(leftHandOperand);
  if (leftHandMap) {
    leftHandMap.set(rightHandOperand, result);
  } else {
    leftHandMap = new MemoizeMap();
    leftHandMap.set(rightHandOperand, result);
    memoizeMap.set(leftHandOperand, leftHandMap);
  }
}

/*!
 * Primary Export
 */

deepEql.exports = deepEqual;
deepEql.exports.MemoizeMap = MemoizeMap;

/**
 * Assert deeply nested sameValue equality between two objects of any type.
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {Object} [options] (optional) Additional options
 * @param {Array} [options.comparator] (optional) Override default algorithm, determining custom equality.
 * @param {Array} [options.memoize] (optional) Provide a custom memoization object which will cache the results of
    complex objects for a speed boost. By passing `false` you can disable memoization, but this will cause circular
    references to blow the stack.
 * @return {Boolean} equal match
 */
function deepEqual(leftHandOperand, rightHandOperand, options) {
  // If we have a comparator, we can't assume anything; so bail to its check first.
  if (options && options.comparator) {
    return extensiveDeepEqual(leftHandOperand, rightHandOperand, options);
  }

  var simpleResult = simpleEqual(leftHandOperand, rightHandOperand);
  if (simpleResult !== null) {
    return simpleResult;
  }

  // Deeper comparisons are pushed through to a larger function
  return extensiveDeepEqual(leftHandOperand, rightHandOperand, options);
}

/**
 * Many comparisons can be canceled out early via simple equality or primitive checks.
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @return {Boolean|null} equal match
 */
function simpleEqual(leftHandOperand, rightHandOperand) {
  // Equal references (except for Numbers) can be returned early
  if (leftHandOperand === rightHandOperand) {
    // Handle +-0 cases
    return leftHandOperand !== 0 || 1 / leftHandOperand === 1 / rightHandOperand;
  }

  // handle NaN cases
  if (
    leftHandOperand !== leftHandOperand && // eslint-disable-line no-self-compare
    rightHandOperand !== rightHandOperand // eslint-disable-line no-self-compare
  ) {
    return true;
  }

  // Anything that is not an 'object', i.e. symbols, functions, booleans, numbers,
  // strings, and undefined, can be compared by reference.
  if (isPrimitive(leftHandOperand) || isPrimitive(rightHandOperand)) {
    // Easy out b/c it would have passed the first equality check
    return false;
  }
  return null;
}

/*!
 * The main logic of the `deepEqual` function.
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {Object} [options] (optional) Additional options
 * @param {Array} [options.comparator] (optional) Override default algorithm, determining custom equality.
 * @param {Array} [options.memoize] (optional) Provide a custom memoization object which will cache the results of
    complex objects for a speed boost. By passing `false` you can disable memoization, but this will cause circular
    references to blow the stack.
 * @return {Boolean} equal match
*/
function extensiveDeepEqual(leftHandOperand, rightHandOperand, options) {
  options = options || {};
  options.memoize = options.memoize === false ? false : options.memoize || new MemoizeMap();
  var comparator = options && options.comparator;

  // Check if a memoized result exists.
  var memoizeResultLeft = memoizeCompare(leftHandOperand, rightHandOperand, options.memoize);
  if (memoizeResultLeft !== null) {
    return memoizeResultLeft;
  }
  var memoizeResultRight = memoizeCompare(rightHandOperand, leftHandOperand, options.memoize);
  if (memoizeResultRight !== null) {
    return memoizeResultRight;
  }

  // If a comparator is present, use it.
  if (comparator) {
    var comparatorResult = comparator(leftHandOperand, rightHandOperand);
    // Comparators may return null, in which case we want to go back to default behavior.
    if (comparatorResult === false || comparatorResult === true) {
      memoizeSet(leftHandOperand, rightHandOperand, options.memoize, comparatorResult);
      return comparatorResult;
    }
    // To allow comparators to override *any* behavior, we ran them first. Since it didn't decide
    // what to do, we need to make sure to return the basic tests first before we move on.
    var simpleResult = simpleEqual(leftHandOperand, rightHandOperand);
    if (simpleResult !== null) {
      // Don't memoize this, it takes longer to set/retrieve than to just compare.
      return simpleResult;
    }
  }

  var leftHandType = type(leftHandOperand);
  if (leftHandType !== type(rightHandOperand)) {
    memoizeSet(leftHandOperand, rightHandOperand, options.memoize, false);
    return false;
  }

  // Temporarily set the operands in the memoize object to prevent blowing the stack
  memoizeSet(leftHandOperand, rightHandOperand, options.memoize, true);

  var result = extensiveDeepEqualByType(leftHandOperand, rightHandOperand, leftHandType, options);
  memoizeSet(leftHandOperand, rightHandOperand, options.memoize, result);
  return result;
}

function extensiveDeepEqualByType(leftHandOperand, rightHandOperand, leftHandType, options) {
  switch (leftHandType) {
    case 'String':
    case 'Number':
    case 'Boolean':
    case 'Date':
      // If these types are their instance types (e.g. `new Number`) then re-deepEqual against their values
      return deepEqual(leftHandOperand.valueOf(), rightHandOperand.valueOf());
    case 'Promise':
    case 'Symbol':
    case 'function':
    case 'WeakMap':
    case 'WeakSet':
      return leftHandOperand === rightHandOperand;
    case 'Error':
      return keysEqual(leftHandOperand, rightHandOperand, [ 'name', 'message', 'code' ], options);
    case 'Arguments':
    case 'Int8Array':
    case 'Uint8Array':
    case 'Uint8ClampedArray':
    case 'Int16Array':
    case 'Uint16Array':
    case 'Int32Array':
    case 'Uint32Array':
    case 'Float32Array':
    case 'Float64Array':
    case 'Array':
      return iterableEqual(leftHandOperand, rightHandOperand, options);
    case 'RegExp':
      return regexpEqual(leftHandOperand, rightHandOperand);
    case 'Generator':
      return generatorEqual(leftHandOperand, rightHandOperand, options);
    case 'DataView':
      return iterableEqual(new Uint8Array(leftHandOperand.buffer), new Uint8Array(rightHandOperand.buffer), options);
    case 'ArrayBuffer':
      return iterableEqual(new Uint8Array(leftHandOperand), new Uint8Array(rightHandOperand), options);
    case 'Set':
      return entriesEqual(leftHandOperand, rightHandOperand, options);
    case 'Map':
      return entriesEqual(leftHandOperand, rightHandOperand, options);
    case 'Temporal.PlainDate':
    case 'Temporal.PlainTime':
    case 'Temporal.PlainDateTime':
    case 'Temporal.Instant':
    case 'Temporal.ZonedDateTime':
    case 'Temporal.PlainYearMonth':
    case 'Temporal.PlainMonthDay':
      return leftHandOperand.equals(rightHandOperand);
    case 'Temporal.Duration':
      return leftHandOperand.total('nanoseconds') === rightHandOperand.total('nanoseconds');
    case 'Temporal.TimeZone':
    case 'Temporal.Calendar':
      return leftHandOperand.toString() === rightHandOperand.toString();
    default:
      return objectEqual(leftHandOperand, rightHandOperand, options);
  }
}

/*!
 * Compare two Regular Expressions for equality.
 *
 * @param {RegExp} leftHandOperand
 * @param {RegExp} rightHandOperand
 * @return {Boolean} result
 */

function regexpEqual(leftHandOperand, rightHandOperand) {
  return leftHandOperand.toString() === rightHandOperand.toString();
}

/*!
 * Compare two Sets/Maps for equality. Faster than other equality functions.
 *
 * @param {Set} leftHandOperand
 * @param {Set} rightHandOperand
 * @param {Object} [options] (Optional)
 * @return {Boolean} result
 */

function entriesEqual(leftHandOperand, rightHandOperand, options) {
  // IE11 doesn't support Set#entries or Set#@@iterator, so we need manually populate using Set#forEach
  if (leftHandOperand.size !== rightHandOperand.size) {
    return false;
  }
  if (leftHandOperand.size === 0) {
    return true;
  }
  var leftHandItems = [];
  var rightHandItems = [];
  leftHandOperand.forEach(function gatherEntries(key, value) {
    leftHandItems.push([ key, value ]);
  });
  rightHandOperand.forEach(function gatherEntries(key, value) {
    rightHandItems.push([ key, value ]);
  });
  return iterableEqual(leftHandItems.sort(), rightHandItems.sort(), options);
}

/*!
 * Simple equality for flat iterable objects such as Arrays, TypedArrays or Node.js buffers.
 *
 * @param {Iterable} leftHandOperand
 * @param {Iterable} rightHandOperand
 * @param {Object} [options] (Optional)
 * @return {Boolean} result
 */

function iterableEqual(leftHandOperand, rightHandOperand, options) {
  var length = leftHandOperand.length;
  if (length !== rightHandOperand.length) {
    return false;
  }
  if (length === 0) {
    return true;
  }
  var index = -1;
  while (++index < length) {
    if (deepEqual(leftHandOperand[index], rightHandOperand[index], options) === false) {
      return false;
    }
  }
  return true;
}

/*!
 * Simple equality for generator objects such as those returned by generator functions.
 *
 * @param {Iterable} leftHandOperand
 * @param {Iterable} rightHandOperand
 * @param {Object} [options] (Optional)
 * @return {Boolean} result
 */

function generatorEqual(leftHandOperand, rightHandOperand, options) {
  return iterableEqual(getGeneratorEntries(leftHandOperand), getGeneratorEntries(rightHandOperand), options);
}

/*!
 * Determine if the given object has an @@iterator function.
 *
 * @param {Object} target
 * @return {Boolean} `true` if the object has an @@iterator function.
 */
function hasIteratorFunction(target) {
  return typeof Symbol !== 'undefined' &&
    typeof target === 'object' &&
    typeof Symbol.iterator !== 'undefined' &&
    typeof target[Symbol.iterator] === 'function';
}

/*!
 * Gets all iterator entries from the given Object. If the Object has no @@iterator function, returns an empty array.
 * This will consume the iterator - which could have side effects depending on the @@iterator implementation.
 *
 * @param {Object} target
 * @returns {Array} an array of entries from the @@iterator function
 */
function getIteratorEntries(target) {
  if (hasIteratorFunction(target)) {
    try {
      return getGeneratorEntries(target[Symbol.iterator]());
    } catch (iteratorError) {
      return [];
    }
  }
  return [];
}

/*!
 * Gets all entries from a Generator. This will consume the generator - which could have side effects.
 *
 * @param {Generator} target
 * @returns {Array} an array of entries from the Generator.
 */
function getGeneratorEntries(generator) {
  var generatorResult = generator.next();
  var accumulator = [ generatorResult.value ];
  while (generatorResult.done === false) {
    generatorResult = generator.next();
    accumulator.push(generatorResult.value);
  }
  return accumulator;
}

/*!
 * Gets all own and inherited enumerable keys from a target.
 *
 * @param {Object} target
 * @returns {Array} an array of own and inherited enumerable keys from the target.
 */
function getEnumerableKeys(target) {
  var keys = [];
  for (var key in target) {
    keys.push(key);
  }
  return keys;
}

function getNonEnumerableSymbols(target) {
  var keys = Object.getOwnPropertySymbols(target);
  return keys;
}

/*!
 * Determines if two objects have matching values, given a set of keys. Defers to deepEqual for the equality check of
 * each key. If any value of the given key is not equal, the function will return false (early).
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {Array} keys An array of keys to compare the values of leftHandOperand and rightHandOperand against
 * @param {Object} [options] (Optional)
 * @return {Boolean} result
 */
function keysEqual(leftHandOperand, rightHandOperand, keys, options) {
  var length = keys.length;
  if (length === 0) {
    return true;
  }
  for (var i = 0; i < length; i += 1) {
    if (deepEqual(leftHandOperand[keys[i]], rightHandOperand[keys[i]], options) === false) {
      return false;
    }
  }
  return true;
}

/*!
 * Recursively check the equality of two Objects. Once basic sameness has been established it will defer to `deepEqual`
 * for each enumerable key in the object.
 *
 * @param {Mixed} leftHandOperand
 * @param {Mixed} rightHandOperand
 * @param {Object} [options] (Optional)
 * @return {Boolean} result
 */
function objectEqual(leftHandOperand, rightHandOperand, options) {
  var leftHandKeys = getEnumerableKeys(leftHandOperand).sort();
  var rightHandKeys = getEnumerableKeys(rightHandOperand).sort();
  var leftHandSymbols = getNonEnumerableSymbols(leftHandOperand).sort();
  var rightHandSymbols = getNonEnumerableSymbols(rightHandOperand).sort();

  leftHandKeys = leftHandKeys.concat(leftHandSymbols);
  rightHandKeys = rightHandKeys.concat(rightHandSymbols);

  if (leftHandKeys.length && leftHandKeys.length === rightHandKeys.length) {
    if (iterableEqual(leftHandKeys, rightHandKeys) === false) {
      return false;
    }
    return keysEqual(leftHandOperand, rightHandOperand, leftHandKeys, options);
  }

  var leftHandEntries = getIteratorEntries(leftHandOperand);
  var rightHandEntries = getIteratorEntries(rightHandOperand);
  if (leftHandEntries.length && leftHandEntries.length === rightHandEntries.length) {
    leftHandEntries.sort();
    rightHandEntries.sort();
    return iterableEqual(leftHandEntries, rightHandEntries, options);
  }

  if (leftHandKeys.length === 0 &&
      leftHandEntries.length === 0 &&
      rightHandKeys.length === 0 &&
      rightHandEntries.length === 0) {
    return true;
  }

  return false;
}

/*!
 * Returns true if the argument is a primitive.
 *
 * This intentionally returns true for all objects that can be compared by reference,
 * including functions and symbols.
 *
 * @param {Mixed} value
 * @return {Boolean} result
 */
function isPrimitive(value) {
  return value === null || typeof value !== 'object';
}

var findOrCreate$1 = {};

Object.defineProperty(findOrCreate$1, "__esModule", { value: true });
findOrCreate$1.findOrCreate = void 0;
/**
 * Find the array entry matching the given predicate. If none match, create a
 * default entry and push it into the array. In either case, the entry will be
 * returned.
 *
 * The predicate receives one parameter: the entry.
 *
 * THIS MODIFIES THE ARRAY.
 *
 * @param arr The array to search.
 * @param predicate The search condition.
 * @param construct The creator function.
 * @returns The entry that was found, or created.
 */
function findOrCreate(arr, predicate, construct) {
    const index = arr.findIndex((entry) => predicate(entry));
    if (index >= 0) {
        return arr[index];
    }
    const newEntry = construct();
    arr.push(newEntry);
    return newEntry;
}
findOrCreate$1.findOrCreate = findOrCreate;

var asArrays = {};

Object.defineProperty(asArrays, "__esModule", { value: true });
asArrays.asArraysFactory = void 0;
/**
 * Create an ArraysCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function asArraysFactory(groups) {
    return () => {
        return groups.map((g) => g.items);
    };
}
asArrays.asArraysFactory = asArraysFactory;

var asEntries = {};

Object.defineProperty(asEntries, "__esModule", { value: true });
asEntries.asEntriesFactory = void 0;
/**
 * Create an EntriesCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function asEntriesFactory(groups) {
    return ((options) => {
        var _a, _b;
        const keyName = (_a = options === null || options === void 0 ? void 0 : options.keyName) !== null && _a !== void 0 ? _a : 'key';
        const itemsName = (_b = options === null || options === void 0 ? void 0 : options.itemsName) !== null && _b !== void 0 ? _b : 'items';
        return groups.map((g) => ({
            [keyName]: g.key,
            [itemsName]: g.items
        }));
    });
}
asEntries.asEntriesFactory = asEntriesFactory;

var asMap = {};

Object.defineProperty(asMap, "__esModule", { value: true });
asMap.asMapFactory = void 0;
/**
 * Create a MapCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function asMapFactory(groups) {
    return () => {
        const map = new Map();
        for (const group of groups) {
            map.set(group.key, group.items);
        }
        return map;
    };
}
asMap.asMapFactory = asMapFactory;

var asObject = {};

Object.defineProperty(asObject, "__esModule", { value: true });
asObject.asObjectFactory = void 0;
/**
 * Determine whether the given key can be used to specify an object property. This is true if, and only if,
 * it is of type string, number or symbol.
 *
 * @param key The key.
 * @returns Whether the key is of a valid type.
 */
function isObjectAssignable(key) {
    return typeof key === 'string' || typeof key === 'number' || typeof key === 'symbol';
}
/**
 * Create an ObjectCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function asObjectFactory(groups) {
    return () => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const obj = {};
        for (const group of groups) {
            if (isObjectAssignable(group.key)) {
                obj[group.key] = group.items;
            }
        }
        return obj;
    };
}
asObject.asObjectFactory = asObjectFactory;

var asTuples = {};

Object.defineProperty(asTuples, "__esModule", { value: true });
asTuples.asTuplesFactory = void 0;
/**
 * Create a TuplesCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function asTuplesFactory(groups) {
    return () => {
        return groups.map((g) => [g.key, g.items]);
    };
}
asTuples.asTuplesFactory = asTuplesFactory;

var keys = {};

Object.defineProperty(keys, "__esModule", { value: true });
keys.keysFactory = void 0;
/**
 * Create a TuplesCollector for the given grouping.
 *
 * @param groups The grouping.
 * @returns The created collector.
 */
function keysFactory(groups) {
    return () => {
        return groups.map((g) => g.key);
    };
}
keys.keysFactory = keysFactory;

var __importDefault = (commonjsGlobal && commonjsGlobal.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(group$1, "__esModule", { value: true });
group$1.group = void 0;
const deep_eql_1 = __importDefault(deepEql.exports);
const find_or_create_js_1 = findOrCreate$1;
const as_arrays_js_1 = asArrays;
const as_entries_js_1 = asEntries;
const as_map_js_1 = asMap;
const as_object_js_1 = asObject;
const as_tuples_js_1 = asTuples;
const keys_js_1 = keys;
// UTILITY METHODS
/**
 * Create a grouping of the items with the given keying function.
 *
 * The keying function receives a single argument - the item - and must return
 * the key to be used for that item.
 *
 * The result of this method is an array of objects containing:
 * - key: the common key value of the items in this group
 * - items: array of items in this group
 *
 * @param items Item iterable.
 * @param keyFn Keying function.
 * @returns Array of groups.
 */
function createGrouping(items, keyFn) {
    const groups = [];
    let idx = 0;
    for (const item of items) {
        const itemKey = keyFn(item, idx);
        idx++;
        const predicate = (g) => (0, deep_eql_1.default)(g.key, itemKey);
        const construct = () => ({ key: itemKey, items: [] });
        (0, find_or_create_js_1.findOrCreate)(groups, predicate, construct).items.push(item);
    }
    return groups;
}
/**
 * Start a grouping over the given items.
 *
 * @param items Some iterable collection of items.
 * @returns A groupable object.
 */
function group(items) {
    const by = (key) => {
        // create grouping with resolved keying function
        const keyFn = typeof key === 'function' ? key : (item) => item[key];
        const groups = createGrouping(items, keyFn);
        // return collectors
        return Object.freeze({
            asArrays: (0, as_arrays_js_1.asArraysFactory)(groups),
            asEntries: (0, as_entries_js_1.asEntriesFactory)(groups),
            asMap: (0, as_map_js_1.asMapFactory)(groups),
            asObject: (0, as_object_js_1.asObjectFactory)(groups),
            asTuples: (0, as_tuples_js_1.asTuplesFactory)(groups),
            keys: (0, keys_js_1.keysFactory)(groups)
        });
    };
    return Object.freeze({ by });
}
group$1.group = group;

(function (exports) {
	Object.defineProperty(exports, "__esModule", { value: true });
	exports.group = void 0;
	// export group function and required types
	var group_js_1 = group$1;
	Object.defineProperty(exports, "group", { enumerable: true, get: function () { return group_js_1.group; } });
} (dist));

const CircularArrowsIcon = (props)=>{
    return /*#__PURE__*/ jsx(SvgIcon, {
        ...props,
        children: /*#__PURE__*/ jsx("path", {
            d: "M 12 2 C 10.615 1.998 9.214625 2.2867656 7.890625 2.8847656 L 8.9003906 4.6328125 C 9.9043906 4.2098125 10.957 3.998 12 4 C 15.080783 4 17.738521 5.7633175 19.074219 8.3222656 L 17.125 9 L 21.25 11 L 22.875 7 L 20.998047 7.6523438 C 19.377701 4.3110398 15.95585 2 12 2 z M 6.5097656 4.4882812 L 2.2324219 5.0820312 L 3.734375 6.3808594 C 1.6515335 9.4550558 1.3615962 13.574578 3.3398438 17 C 4.0308437 18.201 4.9801562 19.268234 6.1601562 20.115234 L 7.1699219 18.367188 C 6.3019219 17.710187 5.5922656 16.904 5.0722656 16 C 3.5320014 13.332354 3.729203 10.148679 5.2773438 7.7128906 L 6.8398438 9.0625 L 6.5097656 4.4882812 z M 19.929688 13 C 19.794687 14.08 19.450734 15.098 18.927734 16 C 17.386985 18.668487 14.531361 20.090637 11.646484 19.966797 L 12.035156 17.9375 L 8.2402344 20.511719 L 10.892578 23.917969 L 11.265625 21.966797 C 14.968963 22.233766 18.681899 20.426323 20.660156 17 C 21.355156 15.801 21.805219 14.445 21.949219 13 L 19.929688 13 z"
        })
    });
};

const objectLb = '{';
const arrayLb = '[';
const objectRb = '}';
const arrayRb = ']';
function inspectMetadata(value) {
    let length;
    let name = '';
    if (Array.isArray(value)) {
        length = value.length;
    } else if (value instanceof Map || value instanceof Set) {
        name = value[Symbol.toStringTag];
        length = value.size;
    } else {
        length = Object.keys(value).length;
    }
    if (Object.prototype.hasOwnProperty.call(value, Symbol.toStringTag)) {
        name = value[Symbol.toStringTag];
    }
    return `${length} Items${name ? ` (${name})` : ''}`;
}
const PreObjectType = (props)=>{
    const metadataColor = useJsonViewerStore((store)=>store.colorspace.base04);
    const textColor = useTextColor();
    const isArray = useMemo(()=>Array.isArray(props.value), [
        props.value
    ]);
    const sizeOfValue = useMemo(()=>props.inspect ? inspectMetadata(props.value) : '', [
        props.inspect,
        props.value
    ]);
    const displayObjectSize = useJsonViewerStore((store)=>store.displayObjectSize);
    const isTrap = useIsCycleReference(props.path, props.value);
    return /*#__PURE__*/ jsxs(Box, {
        component: "span",
        className: "data-object-start",
        sx: {
            letterSpacing: 0.5
        },
        children: [
            isArray ? arrayLb : objectLb,
            displayObjectSize ? /*#__PURE__*/ jsx(Box, {
                component: "span",
                sx: {
                    pl: 0.5,
                    fontStyle: 'italic',
                    color: metadataColor
                },
                children: sizeOfValue
            }) : null,
            isTrap && !props.inspect ? /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx(CircularArrowsIcon, {
                        sx: {
                            fontSize: 12,
                            color: textColor,
                            mx: 0.5
                        }
                    }),
                    isTrap
                ]
            }) : null
        ]
    });
};
const PostObjectType = (props)=>{
    const metadataColor = useJsonViewerStore((store)=>store.colorspace.base04);
    const isArray = useMemo(()=>Array.isArray(props.value), [
        props.value
    ]);
    const displayObjectSize = useJsonViewerStore((store)=>store.displayObjectSize);
    const sizeOfValue = useMemo(()=>!props.inspect ? inspectMetadata(props.value) : '', [
        props.inspect,
        props.value
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        component: "span",
        className: "data-object-end",
        children: [
            isArray ? arrayRb : objectRb,
            displayObjectSize ? /*#__PURE__*/ jsx(Box, {
                component: "span",
                sx: {
                    pl: 0.5,
                    fontStyle: 'italic',
                    color: metadataColor
                },
                children: sizeOfValue
            }) : null
        ]
    });
};
function getIterator(value) {
    return typeof value?.[Symbol.iterator] === 'function';
}
const ObjectType = (props)=>{
    const keyColor = useTextColor();
    const borderColor = useJsonViewerStore((store)=>store.colorspace.base02);
    const groupArraysAfterLength = useJsonViewerStore((store)=>store.groupArraysAfterLength);
    const isTrap = useIsCycleReference(props.path, props.value);
    const [displayLength, setDisplayLength] = useState(useJsonViewerStore((store)=>store.maxDisplayLength));
    const objectSortKeys = useJsonViewerStore((store)=>store.objectSortKeys);
    const elements = useMemo(()=>{
        if (!props.inspect) {
            return null;
        }
        const value = props.value;
        const iterator = getIterator(value);
        // Array also has iterator, we skip it and treat it as an array as normal.
        if (iterator && !Array.isArray(value)) {
            const elements = [];
            if (value instanceof Map) {
                for (const item of value){
                    // fixme: key might be a object, array, or any value for the `Map<any, any>`
                    const [k, value1] = item;
                    const key = `${k}`;
                    elements.push(/*#__PURE__*/ jsx(DataKeyPair, {
                        path: [
                            ...props.path,
                            key
                        ],
                        value: value1,
                        editable: false
                    }, key));
                }
            } else {
                let count = 0;
                for (const item1 of value){
                    elements.push(/*#__PURE__*/ jsx(DataKeyPair, {
                        path: [
                            ...props.path,
                            `iterator:${count}`
                        ],
                        value: item1,
                        nestedIndex: count,
                        editable: false
                    }, count));
                    count++;
                }
            }
            return elements;
        }
        if (Array.isArray(value)) {
            // unknown[]
            if (value.length <= groupArraysAfterLength) {
                const elements1 = value.slice(0, displayLength).map((value, index)=>{
                    const path = [
                        ...props.path,
                        index
                    ];
                    return /*#__PURE__*/ jsx(DataKeyPair, {
                        path: path,
                        value: value
                    }, index);
                });
                if (value.length > displayLength) {
                    const rest = value.length - displayLength;
                    elements1.push(/*#__PURE__*/ jsxs(DataBox, {
                        sx: {
                            cursor: 'pointer',
                            lineHeight: 1.5,
                            color: keyColor,
                            letterSpacing: 0.5,
                            opacity: 0.8
                        },
                        onClick: ()=>setDisplayLength((length)=>length * 2),
                        children: [
                            "hidden ",
                            rest,
                            " items..."
                        ]
                    }, "last"));
                }
                return elements1;
            }
            const elements2 = dist.group(value).by((_, index)=>Math.floor(index / groupArraysAfterLength)).asArrays();
            return elements2.map((list, index)=>{
                const path = [
                    ...props.path
                ];
                return /*#__PURE__*/ jsx(DataKeyPair, {
                    path: path,
                    value: list,
                    nestedIndex: index
                }, index);
            });
        }
        // object
        let entries = Object.entries(value);
        if (objectSortKeys) {
            entries = entries.sort(([a], [b])=>objectSortKeys === true ? a.localeCompare(b) : objectSortKeys(a, b));
        }
        const elements3 = entries.slice(0, displayLength).map(([key, value])=>{
            const path = [
                ...props.path,
                key
            ];
            return /*#__PURE__*/ jsx(DataKeyPair, {
                path: path,
                value: value
            }, key);
        });
        if (entries.length > displayLength) {
            const rest1 = entries.length - displayLength;
            elements3.push(/*#__PURE__*/ jsxs(DataBox, {
                sx: {
                    cursor: 'pointer',
                    lineHeight: 1.5,
                    color: keyColor,
                    letterSpacing: 0.5,
                    opacity: 0.8
                },
                onClick: ()=>setDisplayLength((length)=>length * 2),
                children: [
                    "hidden ",
                    rest1,
                    " items..."
                ]
            }, "last"));
        }
        return elements3;
    }, [
        props.inspect,
        props.value,
        props.path,
        groupArraysAfterLength,
        displayLength,
        keyColor,
        objectSortKeys
    ]);
    const marginLeft = props.inspect ? 0.6 : 0;
    const width = useJsonViewerStore((store)=>store.indentWidth);
    const indentWidth = props.inspect ? width - marginLeft : width;
    return /*#__PURE__*/ jsx(Box, {
        className: "data-object",
        sx: {
            display: props.inspect ? 'block' : 'inline-block',
            pl: props.inspect ? indentWidth - 0.6 : 0,
            marginLeft,
            color: keyColor,
            borderLeft: props.inspect ? `1px solid ${borderColor}` : 'none'
        },
        children: props.inspect ? elements : !isTrap ? /*#__PURE__*/ jsx(Box, {
            component: "span",
            className: "data-object-body",
            onClick: ()=>props.setInspect(true),
            sx: {
                '&:hover': {
                    cursor: 'pointer'
                }
            },
            children: "..."
        }) : null
    });
};

const createTypeRegistryStore = ()=>create(combine({
        registry: []
    }, (set)=>({
            registerTypes: (setState)=>{
                set((state)=>({
                        registry: typeof setState === 'function' ? setState(state.registry) : setState
                    }));
            }
        })));
const { Provider: TypeRegistryProvider , useStore: useTypeRegistryStore , useStoreApi: useTypeRegistryStoreApi  } = createContext();
const objectType = {
    is: (value)=>typeof value === 'object',
    Component: ObjectType,
    PreComponent: PreObjectType,
    PostComponent: PostObjectType
};
function matchTypeComponents(value, path, registry) {
    let potential;
    for (const T of registry){
        if (T.is(value, path)) {
            potential = T;
            if (typeof value === 'object') {
                // early return for case like `null`
                return T;
            }
        }
    }
    if (potential === undefined) {
        if (typeof value === 'object') {
            return objectType;
        }
        throw new DevelopmentError('this is not possible');
    }
    return potential;
}
function useTypeComponents(value, path) {
    const registry = useTypeRegistryStore((store)=>store.registry);
    return useMemo(()=>matchTypeComponents(value, path, registry), [
        value,
        path,
        registry
    ]);
}
function predefined() {
    const types = [];
    function registerType(dataType) {
        function compare(prevProps, nextProps) {
            return Object.is(prevProps.value, nextProps.value) && prevProps.inspect && nextProps.inspect && prevProps.path?.join('.') === nextProps.path?.join('.');
        }
        dataType.Component = /*#__PURE__*/ memo(dataType.Component, compare);
        if (dataType.Editor) {
            dataType.Editor = /*#__PURE__*/ memo(dataType.Editor, function compare(prevProps, nextProps) {
                return Object.is(prevProps.value, nextProps.value);
            });
        }
        if (dataType.PreComponent) {
            dataType.PreComponent = /*#__PURE__*/ memo(dataType.PreComponent, compare);
        }
        if (dataType.PostComponent) {
            dataType.PostComponent = /*#__PURE__*/ memo(dataType.PostComponent, compare);
        }
        types.push(dataType);
    }
    registerType({
        is: (value)=>typeof value === 'boolean',
        ...createEasyType('bool', ({ value  })=>/*#__PURE__*/ jsx(Fragment, {
                children: value ? 'true' : 'false'
            }), {
            colorKey: 'base0E',
            fromString: (value)=>Boolean(value)
        })
    });
    const displayOptions = {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    };
    registerType({
        is: (value)=>value instanceof Date,
        ...createEasyType('date', ({ value  })=>/*#__PURE__*/ jsx(Fragment, {
                children: value.toLocaleTimeString('en-us', displayOptions)
            }), {
            colorKey: 'base0D'
        })
    });
    registerType({
        is: (value)=>value === null,
        ...createEasyType('null', ()=>{
            const backgroundColor = useJsonViewerStore((store)=>store.colorspace.base02);
            return /*#__PURE__*/ jsx(Box, {
                sx: {
                    fontSize: '0.8rem',
                    backgroundColor,
                    fontWeight: 'bold',
                    borderRadius: '3px',
                    padding: '0.5px 2px'
                },
                children: "NULL"
            });
        }, {
            colorKey: 'base08',
            displayTypeLabel: false
        })
    });
    registerType({
        is: (value)=>value === undefined,
        ...createEasyType('undefined', ()=>{
            const backgroundColor = useJsonViewerStore((store)=>store.colorspace.base02);
            return /*#__PURE__*/ jsx(Box, {
                sx: {
                    fontSize: '0.7rem',
                    backgroundColor,
                    borderRadius: '3px',
                    padding: '0.5px 2px'
                },
                children: "undefined"
            });
        }, {
            colorKey: 'base05',
            displayTypeLabel: false
        })
    });
    registerType({
        is: (value)=>typeof value === 'string',
        ...createEasyType('string', (props)=>{
            const [showRest, setShowRest] = useState(false);
            const collapseStringsAfterLength = useJsonViewerStore((store)=>store.collapseStringsAfterLength);
            const value = showRest ? props.value : props.value.slice(0, collapseStringsAfterLength);
            const hasRest = props.value.length > collapseStringsAfterLength;
            return /*#__PURE__*/ jsxs(Box, {
                component: "span",
                sx: {
                    overflowWrap: 'anywhere',
                    cursor: hasRest ? 'pointer' : 'inherit'
                },
                onClick: ()=>{
                    setShowRest((value)=>!value);
                },
                children: [
                    '"',
                    value,
                    hasRest && /*#__PURE__*/ jsx("span", {
                        children: "..."
                    }),
                    '"'
                ]
            });
        }, {
            colorKey: 'base09',
            fromString: (value)=>value
        })
    });
    registerType({
        is: (value)=>typeof value === 'function',
        Component: FunctionType,
        PreComponent: PreFunctionType,
        PostComponent: PostFunctionType
    });
    const isInt = (n)=>n % 1 === 0;
    registerType({
        is: (value)=>typeof value === 'number' && isNaN(value),
        ...createEasyType('NaN', ()=>{
            const backgroundColor = useJsonViewerStore((store)=>store.colorspace.base02);
            return /*#__PURE__*/ jsx(Box, {
                sx: {
                    backgroundColor,
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    borderRadius: '3px'
                },
                children: "NaN"
            });
        }, {
            colorKey: 'base08',
            displayTypeLabel: false
        })
    });
    registerType({
        is: (value)=>typeof value === 'number' && !isInt(value),
        ...createEasyType('float', ({ value  })=>/*#__PURE__*/ jsx(Fragment, {
                children: `${value}`
            }), {
            colorKey: 'base0B',
            fromString: (value)=>parseFloat(value)
        })
    });
    registerType({
        is: (value)=>typeof value === 'number' && isInt(value),
        ...createEasyType('int', ({ value  })=>/*#__PURE__*/ jsx(Fragment, {
                children: `${value}`
            }), {
            colorKey: 'base0F',
            fromString: (value)=>parseInt(value)
        })
    });
    registerType({
        is: (value)=>typeof value === 'bigint',
        ...createEasyType('bigint', ({ value  })=>/*#__PURE__*/ jsx(Fragment, {
                children: `${value}n`
            }), {
            colorKey: 'base0F',
            fromString: (value)=>BigInt(value.replace(/\D/g, ''))
        })
    });
    return types;
}

const IconBox = styled((props)=>/*#__PURE__*/ jsx(Box, {
        ...props,
        component: "span"
    }))`
  cursor: pointer;
  padding-left: 0.7rem;
`;
const DataKeyPair = (props)=>{
    const { value , path , nestedIndex  } = props;
    const propsEditable = props.editable ?? undefined;
    const storeEditable = useJsonViewerStore((store)=>store.editable);
    const editable = useMemo(()=>{
        if (storeEditable === false) {
            return false;
        }
        if (propsEditable === false) {
            // props.editable is false which means we cannot provide the suitable way to edit it
            return false;
        }
        if (typeof storeEditable === 'function') {
            return !!storeEditable(path, value);
        }
        return storeEditable;
    }, [
        path,
        propsEditable,
        storeEditable,
        value
    ]);
    const [tempValue, setTempValue] = useState(typeof value === 'function' ? ()=>value : value);
    const depth = path.length;
    const key = path[depth - 1];
    const hoverPath = useJsonViewerStore((store)=>store.hoverPath);
    const isHover = useMemo(()=>{
        return hoverPath && path.every((value, index)=>value === hoverPath.path[index] && nestedIndex === hoverPath.nestedIndex);
    }, [
        hoverPath,
        path,
        nestedIndex
    ]);
    const setHover = useJsonViewerStore((store)=>store.setHover);
    const root = useJsonViewerStore((store)=>store.value);
    const [inspect, setInspect] = useInspect(path, value, nestedIndex);
    const [editing, setEditing] = useState(false);
    const onChange = useJsonViewerStore((store)=>store.onChange);
    const keyColor = useTextColor();
    const numberKeyColor = useJsonViewerStore((store)=>store.colorspace.base0C);
    const { Component , PreComponent , PostComponent , Editor  } = useTypeComponents(value, path);
    const quotesOnKeys = useJsonViewerStore((store)=>store.quotesOnKeys);
    const rootName = useJsonViewerStore((store)=>store.rootName);
    const isRoot = root === value;
    const isNumberKey = Number.isInteger(Number(key));
    const enableClipboard = useJsonViewerStore((store)=>store.enableClipboard);
    const { copy , copied  } = useClipboard();
    const actionIcons = useMemo(()=>{
        if (editing) {
            return /*#__PURE__*/ jsxs(Fragment, {
                children: [
                    /*#__PURE__*/ jsx(IconBox, {
                        children: /*#__PURE__*/ jsx(Close, {
                            sx: {
                                fontSize: '.8rem'
                            },
                            onClick: ()=>{
                                // abort editing
                                setEditing(false);
                                setTempValue(value);
                            }
                        })
                    }),
                    /*#__PURE__*/ jsx(IconBox, {
                        children: /*#__PURE__*/ jsx(Check, {
                            sx: {
                                fontSize: '.8rem'
                            },
                            onClick: ()=>{
                                // finish editing, save data
                                setEditing(false);
                                onChange(path, value, tempValue);
                            }
                        })
                    })
                ]
            });
        }
        return /*#__PURE__*/ jsxs(Fragment, {
            children: [
                enableClipboard && /*#__PURE__*/ jsx(IconBox, {
                    onClick: (event)=>{
                        event.preventDefault();
                        try {
                            copy(JSON.stringify(typeof value === 'function' ? value.toString() : value, null, '  '));
                        } catch (e) {
                            // in some case, this will throw error
                            // for example: circular structure
                            // fixme: `useAlert` hook
                            console.error(e);
                        }
                    },
                    children: copied ? /*#__PURE__*/ jsx(Check, {
                        sx: {
                            fontSize: '.8rem'
                        }
                    }) : /*#__PURE__*/ jsx(ContentCopy, {
                        sx: {
                            fontSize: '.8rem'
                        }
                    })
                }),
                Editor && editable && /*#__PURE__*/ jsx(IconBox, {
                    onClick: (event)=>{
                        event.preventDefault();
                        setEditing(true);
                    },
                    children: /*#__PURE__*/ jsx(Edit, {
                        sx: {
                            fontSize: '.8rem'
                        }
                    })
                })
            ]
        });
    }, [
        Editor,
        copied,
        copy,
        editable,
        editing,
        enableClipboard,
        onChange,
        path,
        tempValue,
        value
    ]);
    const expandable = !!(PreComponent && PostComponent);
    const KeyRenderer = useJsonViewerStore((store)=>store.keyRenderer);
    const downstreamProps = useMemo(()=>({
            path,
            inspect,
            setInspect,
            value
        }), [
        inspect,
        path,
        setInspect,
        value
    ]);
    return /*#__PURE__*/ jsxs(Box, {
        className: "data-key-pair",
        "data-testid": 'data-key-pair' + path.join('.'),
        onMouseEnter: useCallback(()=>setHover(path, nestedIndex), [
            setHover,
            path,
            nestedIndex
        ]),
        children: [
            /*#__PURE__*/ jsxs(DataBox, {
                component: "span",
                className: "data-key",
                sx: {
                    lineHeight: 1.5,
                    color: keyColor,
                    letterSpacing: 0.5,
                    opacity: 0.8
                },
                onClick: useCallback((event)=>{
                    if (event.isDefaultPrevented()) {
                        return;
                    }
                    setInspect((state)=>!state);
                }, [
                    setInspect
                ]),
                children: [
                    expandable ? inspect ? /*#__PURE__*/ jsx(ExpandMore, {
                        sx: {
                            fontSize: '.8rem',
                            '&:hover': {
                                cursor: 'pointer'
                            }
                        }
                    }) : /*#__PURE__*/ jsx(ChevronRight, {
                        sx: {
                            fontSize: '.8rem',
                            '&:hover': {
                                cursor: 'pointer'
                            }
                        }
                    }) : null,
                    isRoot ? rootName !== false ? quotesOnKeys ? /*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            '"',
                            rootName,
                            '"'
                        ]
                    }) : /*#__PURE__*/ jsx(Fragment, {
                        children: rootName
                    }) : null : KeyRenderer.when(downstreamProps) ? /*#__PURE__*/ jsx(KeyRenderer, {
                        ...downstreamProps
                    }) : nestedIndex === undefined && (isNumberKey ? /*#__PURE__*/ jsx(Box, {
                        component: "span",
                        style: {
                            color: numberKeyColor
                        },
                        children: key
                    }) : quotesOnKeys ? /*#__PURE__*/ jsxs(Fragment, {
                        children: [
                            '"',
                            key,
                            '"'
                        ]
                    }) : /*#__PURE__*/ jsx(Fragment, {
                        children: key
                    })),
                    isRoot ? rootName !== false ? /*#__PURE__*/ jsx(DataBox, {
                        sx: {
                            mx: 0.5
                        },
                        children: ":"
                    }) : null : nestedIndex === undefined && /*#__PURE__*/ jsx(DataBox, {
                        sx: {
                            mx: 0.5
                        },
                        children: ":"
                    }),
                    PreComponent && /*#__PURE__*/ jsx(PreComponent, {
                        ...downstreamProps
                    }),
                    isHover && expandable && inspect && actionIcons
                ]
            }),
            editing && editable ? Editor && /*#__PURE__*/ jsx(Editor, {
                value: tempValue,
                setValue: setTempValue
            }) : Component ? /*#__PURE__*/ jsx(Component, {
                ...downstreamProps
            }) : /*#__PURE__*/ jsx(Box, {
                component: "span",
                className: "data-value-fallback",
                children: `fallback: ${value}`
            }),
            PostComponent && /*#__PURE__*/ jsx(PostComponent, {
                ...downstreamProps
            }),
            isHover && expandable && !inspect && actionIcons,
            isHover && !expandable && actionIcons
        ]
    });
};

const query = '(prefers-color-scheme: dark)';
function useThemeDetector() {
    const [isDark, setIsDark] = useState(false);
    useEffect(()=>{
        const listener = (e)=>{
            setIsDark(e.matches);
        };
        setIsDark(window.matchMedia(query).matches);
        const queryMedia = window.matchMedia(query);
        queryMedia.addEventListener('change', listener);
        return ()=>queryMedia.removeEventListener('change', listener);
    }, []);
    return isDark;
}

/**
 * @internal
 */ function useSetIfNotUndefinedEffect(key, value) {
    const api = useJsonViewerStoreApi();
    useEffect(()=>{
        if (value !== undefined) {
            api.setState({
                [key]: value
            });
        }
    }, [
        key,
        value,
        api
    ]);
}
/**
 * @internal
 */ const JsonViewerInner = (props)=>{
    const api = useJsonViewerStoreApi();
    useSetIfNotUndefinedEffect('value', props.value);
    useSetIfNotUndefinedEffect('editable', props.editable);
    useSetIfNotUndefinedEffect('indentWidth', props.indentWidth);
    useSetIfNotUndefinedEffect('onChange', props.onChange);
    useSetIfNotUndefinedEffect('groupArraysAfterLength', props.groupArraysAfterLength);
    useSetIfNotUndefinedEffect('keyRenderer', props.keyRenderer);
    useSetIfNotUndefinedEffect('maxDisplayLength', props.maxDisplayLength);
    useSetIfNotUndefinedEffect('enableClipboard', props.enableClipboard);
    useSetIfNotUndefinedEffect('rootName', props.rootName);
    useSetIfNotUndefinedEffect('displayDataTypes', props.displayDataTypes);
    useSetIfNotUndefinedEffect('displayObjectSize', props.displayObjectSize);
    useEffect(()=>{
        if (props.theme === 'light') {
            api.setState({
                colorspace: lightColorspace
            });
        } else if (props.theme === 'dark') {
            api.setState({
                colorspace: darkColorspace
            });
        } else if (typeof props.theme === 'object') {
            api.setState({
                colorspace: props.theme
            });
        }
    }, [
        api,
        props.theme
    ]);
    const onceRef = useRef(true);
    const predefinedTypes = useMemo(()=>predefined(), []);
    const registerTypes = useTypeRegistryStore((store)=>store.registerTypes);
    if (onceRef.current) {
        const allTypes = [
            ...predefinedTypes
        ];
        props.valueTypes?.forEach((type)=>{
            allTypes.push(type);
        });
        registerTypes(allTypes);
        onceRef.current = false;
    }
    useEffect(()=>{
        const allTypes = [
            ...predefinedTypes
        ];
        props.valueTypes?.forEach((type)=>{
            allTypes.push(type);
        });
        registerTypes(allTypes);
    }, [
        predefinedTypes,
        props.valueTypes,
        registerTypes
    ]);
    const value = useJsonViewerStore((store)=>store.value);
    const setHover = useJsonViewerStore((store)=>store.setHover);
    return /*#__PURE__*/ jsx(Paper, {
        elevation: 0,
        className: props.className,
        style: props.style,
        sx: {
            fontFamily: 'monospace',
            userSelect: 'none',
            contentVisibility: 'auto'
        },
        onMouseLeave: useCallback(()=>{
            setHover(null);
        }, [
            setHover
        ]),
        children: /*#__PURE__*/ jsx(DataKeyPair, {
            value: value,
            path: useMemo(()=>[], [])
        })
    });
};
const JsonViewer = function JsonViewer(props) {
    const isAutoDarkTheme = useThemeDetector();
    const themeType = useMemo(()=>props.theme === 'auto' ? isAutoDarkTheme ? 'light' : 'dark' : props.theme ?? 'light', [
        isAutoDarkTheme,
        props.theme
    ]);
    const theme = useMemo(()=>{
        const backgroundColor = typeof themeType === 'object' ? themeType.base00 : themeType === 'dark' ? darkColorspace.base00 : lightColorspace.base00;
        return createTheme({
            components: {
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundColor
                        }
                    }
                }
            },
            palette: {
                mode: themeType === 'dark' ? 'dark' : 'light',
                background: {
                    default: backgroundColor
                }
            }
        });
    }, [
        themeType
    ]);
    const mixedProps = {
        ...props,
        theme: themeType
    };
    return /*#__PURE__*/ jsx(ThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ jsx(TypeRegistryProvider, {
            createStore: createTypeRegistryStore,
            children: /*#__PURE__*/ jsx(JsonViewerProvider, {
                createStore: ()=>{
                    // This function only runs once, so we don't need a memo for this.
                    //  Refs: https://github.com/pmndrs/zustand/blob/77d14b17bc33a6f10f072802fac56aa78510710e/src/context.ts#L36-L38
                    return createJsonViewerStore(props);
                },
                children: /*#__PURE__*/ jsx(JsonViewerInner, {
                    ...mixedProps
                })
            })
        })
    });
};

export { JsonViewer, applyValue, createDataType, darkColorspace, isCycleReference, lightColorspace };
